import { z } from 'zod';
import { insuranceClaimsCommandReasonSchema, insuranceClaimsCommandReceiptSchema, insuranceClaimsIdSchema, insuranceClaimsPageSchema, } from './insurance-claims-claim.contract.js';
export const insuranceClaimsComplianceReviewsCapability = 'insurance-claims.compliance-reviews.v1';
export const insuranceClaimsComplianceReviewVersionsCapability = 'insurance-claims.compliance-review-versions.v1';
export const insuranceClaimsComplianceReadinessCapability = 'insurance-claims.compliance-readiness.v1';
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
export const insuranceClaimsComplianceReviewStateSchema = z.enum([
    'draft', 'needs-review', 'approved', 'blocked', 'retired',
]);
export const insuranceClaimsComplianceGuidanceModeSchema = z.enum([
    'contractor-documentation', 'referral-required', 'disabled',
]);
export const insuranceClaimsComplianceSourceAuthoritySchema = z.enum([
    'official', 'counsel', 'internal-policy',
]);
export const insuranceClaimsComplianceSourceSchema = z.strictObject({
    sourceKey: z.string().uuid(),
    authority: insuranceClaimsComplianceSourceAuthoritySchema,
    title: z.string().trim().min(1).max(240),
    sourceUrl: z.string().url().max(1_000).nullable(),
    internalReference: nullableText(500),
    accessedOn: z.string().date(),
    note: nullableText(2_000),
}).superRefine((value, context) => {
    if (value.sourceUrl === null && value.internalReference === null) {
        context.addIssue({
            code: 'custom',
            path: ['sourceUrl'],
            message: 'A source URL or internal reference is required.',
        });
    }
});
export const insuranceClaimsComplianceGuardrailsSchema = z.strictObject({
    publicAdjusterBoundarySummary: nullableText(4_000),
    customerDisclaimer: nullableText(4_000),
    supplementLanguageNotes: nullableText(4_000),
    deductibleGuardrail: nullableText(4_000),
    mortgageCheckGuardrail: nullableText(4_000),
});
export const insuranceClaimsComplianceDecisionSchema = z.strictObject({
    kind: z.enum(['approved', 'blocked']),
    decidedAt: z.string().datetime({ offset: true }),
    decidedByMembershipId: insuranceClaimsIdSchema,
});
export const insuranceClaimsComplianceReviewDataSchema = z.strictObject({
    jurisdictionState: z.string().regex(/^[A-Z]{2}$/),
    jurisdictionName: z.string().trim().min(1).max(160),
    guidanceMode: insuranceClaimsComplianceGuidanceModeSchema,
    sources: z.array(insuranceClaimsComplianceSourceSchema).max(20),
    guardrails: insuranceClaimsComplianceGuardrailsSchema,
    effectiveOn: z.string().date().nullable(),
    reviewDueOn: z.string().date().nullable(),
    decision: insuranceClaimsComplianceDecisionSchema.nullable(),
}).superRefine((value, context) => {
    if (value.effectiveOn !== null && value.reviewDueOn !== null && value.reviewDueOn < value.effectiveOn) {
        context.addIssue({ code: 'custom', path: ['reviewDueOn'], message: 'Review due date cannot precede the effective date.' });
    }
});
const complianceDraftShape = {
    jurisdictionState: z.string().regex(/^[A-Z]{2}$/),
    jurisdictionName: z.string().trim().min(1).max(160),
    guidanceMode: insuranceClaimsComplianceGuidanceModeSchema,
    sources: z.array(insuranceClaimsComplianceSourceSchema).max(20),
    guardrails: insuranceClaimsComplianceGuardrailsSchema,
    effectiveOn: z.string().date().nullable().default(null),
    reviewDueOn: z.string().date().nullable().default(null),
};
export const insuranceClaimsComplianceReviewDraftSchema = z.strictObject(complianceDraftShape);
export const insuranceClaimsCreateComplianceReviewCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: insuranceClaimsIdSchema,
    reviewId: insuranceClaimsIdSchema,
    review: insuranceClaimsComplianceReviewDraftSchema,
    reason: insuranceClaimsCommandReasonSchema,
});
const complianceCommandBase = {
    commandId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: insuranceClaimsCommandReasonSchema,
};
export const insuranceClaimsReviseComplianceReviewCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    ...complianceCommandBase,
    review: insuranceClaimsComplianceReviewDraftSchema,
});
export const insuranceClaimsSubmitComplianceReviewCommandSchema = z.strictObject({
    kind: z.literal('submit-review'),
    ...complianceCommandBase,
});
export const insuranceClaimsApproveComplianceReviewCommandSchema = z.strictObject({
    kind: z.literal('approve'),
    ...complianceCommandBase,
    effectiveOn: z.string().date(),
    reviewDueOn: z.string().date().nullable().default(null),
});
export const insuranceClaimsBlockComplianceReviewCommandSchema = z.strictObject({
    kind: z.literal('block'),
    ...complianceCommandBase,
});
export const insuranceClaimsComplianceReviewStateCommandSchema = z.strictObject({
    kind: z.enum(['retire', 'restore']),
    ...complianceCommandBase,
});
export const insuranceClaimsComplianceReviewCommandSchema = z.discriminatedUnion('kind', [
    insuranceClaimsReviseComplianceReviewCommandSchema,
    insuranceClaimsSubmitComplianceReviewCommandSchema,
    insuranceClaimsApproveComplianceReviewCommandSchema,
    insuranceClaimsBlockComplianceReviewCommandSchema,
    insuranceClaimsComplianceReviewStateCommandSchema,
]);
export const insuranceClaimsComplianceReviewVersionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsComplianceReviewVersionsCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    reviewId: insuranceClaimsIdSchema,
    revision: z.number().int().positive(),
    state: insuranceClaimsComplianceReviewStateSchema,
    commandKind: z.enum(['create', 'revise', 'submit-review', 'approve', 'block', 'retire', 'restore']),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: insuranceClaimsCommandReasonSchema,
    actorMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    data: insuranceClaimsComplianceReviewDataSchema,
});
export const insuranceClaimsComplianceReviewSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsComplianceReviewsCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    jurisdictionState: z.string().regex(/^[A-Z]{2}$/),
    state: insuranceClaimsComplianceReviewStateSchema,
    revision: z.number().int().positive(),
    currentVersionId: insuranceClaimsIdSchema,
    createdByMembershipId: insuranceClaimsIdSchema,
    updatedByMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: insuranceClaimsComplianceReviewVersionSchema,
});
export const insuranceClaimsComplianceReadinessSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsComplianceReadinessCapability),
    status: z.enum(['ready', 'not-ready', 'blocked']),
    activeReviewCount: z.number().int().nonnegative(),
    approvedCount: z.number().int().nonnegative(),
    blockedCount: z.number().int().nonnegative(),
    needsReviewCount: z.number().int().nonnegative(),
    missingSourceCount: z.number().int().nonnegative(),
    incompleteGuardrailCount: z.number().int().nonnegative(),
    expiredReviewCount: z.number().int().nonnegative(),
});
export const insuranceClaimsComplianceReviewListQuerySchema = z.strictObject({
    state: insuranceClaimsComplianceReviewStateSchema.nullable().default(null),
    jurisdictionState: z.string().regex(/^[A-Z]{2}$/).nullable().default(null),
    includeRetired: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const insuranceClaimsComplianceReviewResponseSchema = z.strictObject({
    data: insuranceClaimsComplianceReviewSchema,
    command: insuranceClaimsCommandReceiptSchema.optional(),
});
export const insuranceClaimsComplianceReviewsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsComplianceReviewSchema),
    readiness: insuranceClaimsComplianceReadinessSchema,
    page: insuranceClaimsPageSchema,
});
export const insuranceClaimsComplianceReviewVersionsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsComplianceReviewVersionSchema),
});
//# sourceMappingURL=insurance-claims-compliance-review.contract.js.map